export const KEYBOARD_OVERLAP_MIN_PX = 80;

/**
 * Keeps the app shell aligned with the visible iOS Safari viewport.
 * `100dvh` still leaves a gap under the dynamic toolbar / keyboard; visualViewport does not.
 */
export class ViewportHeightSync {
  private attached: boolean = false;

  attach(): void {
    if (this.attached) {
      return;
    }
    this.attached = true;
    this.sync();
    window.addEventListener('resize', this.sync);
    window.visualViewport?.addEventListener('resize', this.sync);
    window.visualViewport?.addEventListener('scroll', this.sync);
  }

  detach(): void {
    if (!this.attached) {
      return;
    }
    this.attached = false;
    window.removeEventListener('resize', this.sync);
    window.visualViewport?.removeEventListener('resize', this.sync);
    window.visualViewport?.removeEventListener('scroll', this.sync);
  }

  private sync = (): void => {
    const box = this.viewportBox();
    this.writeVars(box.height, box.offsetTop, this.keyboardInset(box.height, box.offsetTop));
    this.resetWindowScroll();
  };

  private viewportBox(): { height: number; offsetTop: number } {
    const viewport = window.visualViewport;
    return {
      height: Math.round(viewport?.height ?? window.innerHeight),
      offsetTop: Math.round(viewport?.offsetTop ?? 0),
    };
  }

  private keyboardInset(height: number, offsetTop: number): number {
    const overlap = Math.max(0, window.innerHeight - height - offsetTop);
    return overlap > KEYBOARD_OVERLAP_MIN_PX ? overlap : 0;
  }

  private writeVars(height: number, offsetTop: number, keyboard: number): void {
    const root = document.documentElement;
    root.style.setProperty('--app-height', `${height}px`);
    root.style.setProperty('--vh', `${height * 0.01}px`);
    root.style.setProperty('--vv-top', `${offsetTop}px`);
    root.style.setProperty('--kb', `${keyboard}px`);
  }

  private resetWindowScroll(): void {
    if (window.scrollY !== 0 || window.scrollX !== 0) {
      window.scrollTo(0, 0);
    }
  }
}
